import type { ReactNode } from 'react';
import type { Rollout } from '../../api/types';
import { useI18n } from '../../i18n/useI18n';
import { StatusBadge } from '../../ui/StatusBadge';
import { rolloutStateLabelKey, rolloutStateTone, stillGoing, waveLabelKey } from './rollout';

/**
 * The rollout on record, in one line: none, one going, or one that ended.
 *
 * Three shapes of one line rather than three cards. The difference between them
 * is a badge and a date, and a card each was three quarters of a screen before
 * the first row of anything.
 *
 * It stands on two screens, which is why it is a component rather than a piece
 * of the board: an operator opening the list of tenancies should be able to see
 * that the fleet is moving without going to look for it. There it carries the
 * way to the board and leaves out the particulars; on the board itself the
 * particulars are what somebody came for.
 */
export function RolloutLine({
  rollout,
  moving,
  detail = false,
  link,
}: {
  rollout: Rollout | undefined;
  /** Whether a wave is being moved this second. The dot, and nothing else. */
  moving: boolean;
  /** Whether to carry who started it, when it last moved, and the server's own line. */
  detail?: boolean;
  /** The way to the board, where the reader is not already on it. */
  link?: ReactNode;
}) {
  const { t, formatDateTime } = useI18n();

  if (rollout === undefined) {
    return (
      <div className="state-line">
        <StatusBadge tone="neutral" label={t('rollout.none.title')} />
        {link}
      </div>
    );
  }

  const ended = !stillGoing(rollout);

  return (
    <div className="state-line">
      <StatusBadge
        tone={rolloutStateTone(rollout.state)}
        label={t(rolloutStateLabelKey(rollout.state))}
      />
      <span className="mono">{rollout.target}</span>
      <span className="sep" aria-hidden="true">
        ·
      </span>
      <span>{t(waveLabelKey(rollout.wave))}</span>
      {detail && (
        <>
          <span className="sep" aria-hidden="true">
            ·
          </span>
          <span className="faint">{t('rollout.field.actor')}</span>
          <span className="mono">{rollout.actor}</span>
          <span className="sep" aria-hidden="true">
            ·
          </span>
          <span className="faint">
            {t(ended ? 'rollout.field.finished' : 'rollout.field.changed')}
          </span>
          <span>
            {ended
              ? rollout.finished_at === undefined
                ? t('rollout.field.finished.untold')
                : formatDateTime(rollout.finished_at)
              : formatDateTime(rollout.changed_at)}
          </span>
        </>
      )}
      {moving && <span className="live-dot" aria-hidden="true" />}
      {link}
      {detail && <p className="detail">{rollout.detail}</p>}
    </div>
  );
}
